import { Bell, CalendarClock, CheckCircle2, CircleDollarSign, FileClock, FileWarning, PackageSearch, Play } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "./api";
import { notificationContent } from "./notification-content";
import { notificationsChangedEvent, notifyNotificationsChanged } from "./notification-events";
import { notificationTarget } from "./notification-routing";
import type { Notification, NotificationPage, NotificationTemplateCode } from "./types";

const icons: Record<NotificationTemplateCode, typeof Bell> = {
  appointment_assigned: CalendarClock,
  appointment_rescheduled: CalendarClock,
  appointment_cancelled: CalendarClock,
  appointment_reminder_day: CalendarClock,
  appointment_reminder_hour: CalendarClock,
  appointment_late: FileWarning,
  weekly_schedule_summary: CalendarClock,
  quote_accepted: Bell,
  payment_overdue: CircleDollarSign,
  work_order_started: Play,
  work_order_completed: CheckCircle2,
  work_order_document_pending: FileWarning,
  inventory_below_minimum: PackageSearch,
  document_expiring: FileClock,
};

export function NotificationCenter() {
  const navigate = useNavigate();
  const [page, setPage] = useState<NotificationPage>({ unreadCount: 0, counts: { all: 0, unread: 0, read: 0, resolved: 0 }, data: [] });
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try { setPage(await api<NotificationPage>("/notifications?state=active&pageSize=8")); }
    catch { /* Alertas não bloqueiam a navegação. */ }
  }, []);

  useEffect(() => {
    void load();
    const interval = window.setInterval(() => void load(), 30_000);
    const onFocus = () => void load();
    const onVisibility = () => { if (document.visibilityState === "visible") void load(); };
    window.addEventListener("focus", onFocus);
    window.addEventListener("visibilitychange", onVisibility);
    window.addEventListener(notificationsChangedEvent, onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener(notificationsChangedEvent, onFocus);
    };
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeEscape = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    window.addEventListener("pointerdown", closeOutside);
    window.addEventListener("keydown", closeEscape);
    return () => { window.removeEventListener("pointerdown", closeOutside); window.removeEventListener("keydown", closeEscape); };
  }, [open]);

  const markRead = async (notification: Notification) => {
    if (notification.readAt) return;
    const readAt = new Date().toISOString();
    setPage((current) => ({
      unreadCount: Math.max(0, current.unreadCount - 1),
      counts: { ...current.counts, unread: Math.max(0, current.counts.unread - 1), read: current.counts.read + 1 },
      data: current.data.map((item) => item.id === notification.id ? { ...item, readAt } : item),
    }));
    try { await api(`/notifications/${notification.id}/read`, { method: "POST" }); notifyNotificationsChanged(); }
    catch { void load(); }
  };

  const openNotification = async (notification: Notification) => {
    await markRead(notification);
    setOpen(false);
    navigate(notificationTarget(notification));
  };

  return <div className="notification-center" ref={root}>
    <button className="notification-trigger" type="button" onClick={() => setOpen((value) => { if (!value) void load(); return !value; })} aria-label={`${page.unreadCount} ${page.unreadCount === 1 ? "notificação não lida" : "notificações não lidas"}`} aria-expanded={open} aria-controls="notification-popover">
      <Bell size={19} />
      {page.unreadCount > 0 && <span>{page.unreadCount > 9 ? "9+" : page.unreadCount}</span>}
    </button>
    {open && <section className="notification-popover" id="notification-popover" aria-label="Notificações">
      <header><h2>Notificações</h2>{page.unreadCount > 0 && <small>{page.unreadCount} nova{page.unreadCount > 1 ? "s" : ""}</small>}</header>
      {page.data.length === 0 ? <div className="notification-empty"><Bell size={20} /><span>Tudo em dia</span></div> : <div className="notification-list">
        {page.data.map((notification) => {
          const Icon = icons[notification.templateCode];
          const content = notificationContent(notification);
          return <button type="button" key={notification.id} className={notification.readAt ? "" : "unread"} onClick={() => void openNotification(notification)}>
            <span className="notification-icon"><Icon size={17} /></span>
            <span><strong>{content.title}</strong><small>{content.detail}</small></span>
            {!notification.readAt && !notification.resolvedAt && <i aria-label="Não lida" />}
          </button>;
        })}
      </div>}
      <button className="notification-view-all" type="button" onClick={() => { setOpen(false); navigate("/notificacoes"); }}>Ver todas as notificações</button>
    </section>}
  </div>;
}
